import { useContext, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../../../../utils/axiosInstance";
import { AuthContext } from "../../../../../context/AuthContext";
import { isFreelance } from "../../../../../utils/constants/accountType";
import { btn } from "../../../../../components/Button/buttonStyles";
import { toast } from "../../../../../components/Toast/toast";
import { QuotationStatusBadge } from "../../../../../components/Status/QuotationStatus";
import { personName, formatDateTime, formatMoney } from "../../quotationFormat";
import { Card, Field } from "./DetailFields";
import LinkProjectModal from "./LinkProjectModal";

// Unlinking stays available while locked; it leaves the status as Accepted.
const QuotationStatusCard = ({ quotation, onChange }) => {
  const { user } = useContext(AuthContext);
  // Freelance accounts have no managers, so the card is status only.
  const freelance = isFreelance(user);

  const [linkOpen, setLinkOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const money = (value) => `${formatMoney(value)}${quotation.currency ? ` ${quotation.currency}` : ""}`;

  const createProject = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/quotations/${quotation.id}/create-project`);
      onChange(res.data.data);
      toast.success(res.data.message);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to create the project");
    } finally {
      setBusy(false);
    }
  };

  const unlink = async () => {
    setBusy(true);
    try {
      const res = await api.put(`/quotations/${quotation.id}/project`, { project_id: null });
      onChange(res.data.data);
      toast.success(res.data.message);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to unlink the project");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section>
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-xl font-bold text-gray-900">{freelance ? "Status" : "Manager & Status"}</h2>
      </div>

      <Card>
        {!freelance && <Field label="Primary Manager" value={personName(quotation.primaryManager)} />}

        <Field
          label="Linked Project"
          value={
            quotation.project ? (
              <span className="flex items-center gap-3 flex-wrap">
                <Link
                  to={`/project/${quotation.project.id}`}
                  className="text-brand-600 hover:text-brand-800 underline font-medium"
                >
                  {quotation.project.project_name}
                </Link>
                <button onClick={unlink} disabled={busy} className={btn("secondary", "sm")}>
                  Unlink
                </button>
              </span>
            ) : (
              <span className="flex items-center gap-2 flex-wrap">
                <button onClick={createProject} disabled={busy} className={btn("primary", "sm")}>
                  {busy ? "Creating..." : "Create Project"}
                </button>
                <button onClick={() => setLinkOpen(true)} disabled={busy} className={btn("primary", "sm")}>
                  Link Project
                </button>
              </span>
            )
          }
        />

        <Field label="Status" value={<QuotationStatusBadge status={quotation.status} />} />
        <Field label="Closed At" value={formatDateTime(quotation.closed_at)} />
        <Field label="Expire At" value={formatDateTime(quotation.expire_at)} />
        <Field label="Subtotal" value={money(quotation.subtotal)} />
        <Field label="Taxes" value={money(quotation.tax_total)} />
        <Field label="Total" value={<span className="font-semibold">{money(quotation.total)}</span>} />
      </Card>

      {linkOpen && (
        <LinkProjectModal
          quotationId={quotation.id}
          onClose={() => setLinkOpen(false)}
          onLinked={(updated) => {
            setLinkOpen(false);
            onChange(updated);
          }}
        />
      )}
    </section>
  );
};

export default QuotationStatusCard;
